<template>
  <div>
    <p v-if="displayType === 'long'">
      Risk scores are calibrated to match the drawdown of standard portfolio allocations, based on the table below. The allocations for each asset class are represented by broad market indexes.
      0 drawdown is mapped to a risk score of 0, and 100% drawdown is mapped to a risk score of 100.

      Risk Score | Allocation Category | Cash Allocation | Fixed Income Allocation | Equity Allocation
      10 | Conservative | 2% | 78% | 20%
      30 | Moderately Conservative | 1% | 59% | 40%
      50 | Moderate | 1% | 39% | 60%
      70 | Moderately Aggressive | 0% | 20% | 80%
      90 | Aggressive | 0% | 5% | 95%

      The full range of risk scores are derived from drawdown using a one-dimensional linear interpolation function to estimate values based on the calibration data.

      Drawdown is calculated using a monte carlo simulation of portfolio returns, looking at the 95th percentile of drawdowns. To calculate the portfolio drawdown, we perform the following steps:

      Imputed Return Calculation:
      Where returns are available for the last 20 years, we use real return data.
      If returns data are available for at least 100 days, we run a regression of the return data with securities that have been around for 20 years or more. We then use the regression coefficients to impute returns.
      We then sample from the difference between the imputed returns and the actual returns to simulate the uncertainty in the imputed returns. We sample from the differences add to imputed returns for where no return data is available. Where return data is available, we take real returns.
      We ignore securities where returns data are not available, or are only available for less than 100 days.

      Random Selection:
      We augment our sample with negative imputed returns to ensure our sample has returns with mean 0.
      We randomly select return samples from the portfolio's imputed returns
      The selection is done 100,000 times with each sample containing 126 data points.
      This randomization simulates various possible scenarios for the portfolio's return pattern over a given time frame.

      Cumulative Returns:
      For each set of random samples, we calculate the cumulative returns.
      This is done by multiplying the returns in a compounded manner.
      The formula for each step is:
      CRi = CR(i-1) x (1+Ri), where CRi is cumulative return at step i, CR(i-1) is cumulative return at step i-1, and Ri is return at step i.

      Minimum Cumulative Return:
      For each set of 126 cumulative returns, we find the minimum value.
      This represents the worst cumulative return for that random scenario.

      Sorting:
      All minimum cumulative returns (from the 100,000 random scenarios) are then sorted in ascending order.

      Drawdown Calculation:
      We then pick the 500th value from the sorted list, representing the 0.5 percentile of worst scenarios.
      The drawdown is calculated as:
      Drawdown = 1 - 500th value.

      This drawdown value represents a severe but plausible worst-case scenario for the portfolio, considering only 0.5% of scenarios have a worse drawdown.

      The portfolio risk scores apply to the portion of each portfolio with returns coverage, which includes Equities, Mutual Funds, ETFs, and Cash.
      <template v-if="portfolio && benchmark">
        {{ coverage.portfolio.toLocaleString('en-US', { style: 'percent' }) }} for {{ portfolioName }}, and
        {{ coverage.benchmark.toLocaleString('en-US', { style: 'percent' }) }} for {{ benchmarkName }}.
      </template>
      <template v-else-if="portfolio">
        {{ coverage.portfolio.toLocaleString('en-US', { style: 'percent' }) }} for {{ portfolioName }}.
      </template>
      <template v-else-if="benchmark">
        {{ coverage.benchmark.toLocaleString('en-US', { style: 'percent' }) }} for {{ benchmarkName }}.
      </template>
    </p>

    <p v-else>
      Portfolio risk scores mapped to the drawdown of the portfolio, and calibrated based on standard portfolio allocations (see the table below). Drawdown is calculated using a monte carlo simulation of portfolio returns, looking at the 95th percentile of drawdowns.
      The portfolio risk scores apply to the portion of each portfolio with returns coverage, which includes Equities, Mutual Funds, ETFs, and Cash.
      <template v-if="portfolio && benchmark">
        {{ coverage.portfolio.toLocaleString('en-US', { style: 'percent' }) }} for {{ portfolioName }}, and
        {{ coverage.benchmark.toLocaleString('en-US', { style: 'percent' }) }} for {{ benchmarkName }}.
      </template>
      <template v-else-if="portfolio">
        {{ coverage.portfolio.toLocaleString('en-US', { style: 'percent' }) }} for {{ portfolioName }}.
      </template>
      <template v-else-if="benchmark">
        {{ coverage.benchmark.toLocaleString('en-US', { style: 'percent' }) }} for {{ benchmarkName }}.
      </template>
      See full methodology below.
    </p>
  </div>
</template>

<script>
export default {
  name: 'RiskMethodology',
  props: ['displayType', 'portfolio', 'benchmark'],
  computed: {
    portfolioName() {
      return this.portfolio?.name || 'Portfolio';
    },
    benchmarkName() {
      return this.benchmark?.name || 'Benchmark';
    },
    rebalance() {
      return {
        portfolio: this.portfolio?.trailing_returns_monthly_rebalance,
        benchmark: this.benchmark?.trailing_returns_monthly_rebalance,
      };
    },
    coverage() {
      return {
        portfolio: this.rebalance?.portfolio?.[2] || 0,
        benchmark: this.rebalance?.benchmark?.[2] || 0,
      };
    },
  },
};
</script>
